import { CIRCLE_EXPERTS_STEP } from "./circle-activists-copy";
/** Все тексты нового мастера, «Первых шагов» и подсказок MrDrive в разделах. */
import type { PlatformIntent, FirstStepId } from "@/lib/platform-intents";
import { ER_HINT } from "@/lib/expert-requests-copy";

export const WIZARD_COPY = {
  intentsTitle: "Что ты хочешь делать в TurkeyDrive?",
  intentsHint: "Можно выбрать несколько. От этого зависит, что я покажу тебе первым.",
  extraLabel: "А ещё мне интересно",
  cityTitle: "Где ты в Турции?",
  cityHint: "Покажу события твоего города.",
  touristLabel: "Я турист, бываю в разных городах",
  interestsTitle: "Что тебе интересно?",
  interestsHint: "Выбери хотя бы 3. По ним я буду подбирать события и людей, с кем можно познакомиться.",
  next: "Дальше",
  done: "Готово",
  finalTitle: (name: string) => (name ? `Добро пожаловать в сообщество TurkeyDrive, ${name}!` : "Добро пожаловать в сообщество TurkeyDrive!"),
  finalText: (city: string | null) =>
    city
      ? `Теперь ты среди своих. Я собрал для тебя события в городе ${city}.`
      : "Теперь ты среди своих.",
  enablePush: "Включить уведомления",
  pushOn: "Уведомления включены",
  pushFailed: "Не получилось включить уведомления. Можно сделать это позже в профиле",
  iosInstallText: "Чтобы получать уведомления, добавь TurkeyDrive на экран телефона.",
  iosInstallButton: "Покажу, как",
  installTitle: "Добавь TurkeyDrive на экран телефона",
  openEvents: "Смотреть афишу",
  createEvent: "Добавить своё событие",
  createCircle: "Создать сообщество",
  fillExpertCard: "Заполнить карточку эксперта",
  eventsFirst: "Сначала посмотреть афишу",
  meetIntro: "Чтобы и тебя нашли",
} as const;

export const INTENT_CARDS: { value: PlatformIntent; title: string; text: string }[] = [
  { value: "attend", title: "Ходить на события", text: "Узнавать, куда пойти в своём городе" },
  { value: "organize", title: "Организовывать события", text: "Собирать людей на свои встречи" },
  {
    value: "community",
    title: "Вести сообщество",
    text: "Объединять своих вокруг дела или интереса",
  },
  { value: "expert", title: "Делиться экспертизой", text: "Найти свою аудиторию и выступать" },
];

export const INTENT_CHIPS: { value: PlatformIntent; label: string }[] = [
  { value: "meet", label: "Познакомиться с новыми людьми" },
  { value: "advice", label: "Спросить совета у своих" },
  { value: "hire", label: "Найти сотрудника или исполнителя" },
  { value: "market", label: "Купить или продать вещи" },
];

export const INTENT_LABELS: Record<PlatformIntent, string> = {
  attend: "Ходить на события",
  organize: "Организовывать события",
  community: "Вести сообщество",
  expert: "Делиться экспертизой",
  meet: "Познакомиться",
  advice: "Спросить совета",
  hire: "Найти сотрудника",
  market: "Купить или продать",
};

export const FIRST_STEPS_TITLE = "Первые шаги в TurkeyDrive";
export const firstStepsCounter = (x: number, n: number) => `Шаг ${x} из ${n}`;

export const FIRST_STEP_COPY: Record<FirstStepId, { title: string; text?: string; cta: string }> = {
  events_open: {
    title: "Посмотри афишу",
    text: "Все события твоего города в одном месте",
    cta: "Открыть афишу",
  },
  install: {
    title: "Добавь TurkeyDrive на экран телефона",
    text: "Так на iPhone приходят уведомления о новых событиях в твоём городе",
    cta: "Показать, как",
  },
  push: {
    title: "Включи уведомления о новых событиях",
    text: "Не пропустишь новые события по своим интересам",
    cta: "Включить",
  },
  event_create: {
    title: "Добавь своё событие",
    text: "Пара минут, и его увидят все в твоём городе",
    cta: "Добавить событие",
  },
  event_share: {
    title: "Поделись ссылкой в своём канале",
    text: "Люди посмотрят детали и запишутся",
    cta: "Скопировать ссылку",
  },
  circle_create: {
    title: "Создай сообщество",
    text: "Страница твоего движения или клуба в приложении",
    cta: "Создать",
  },
  circle_share: {
    title: "Пригласи своих",
    text: "Отправь ссылку на сообщество в свой чат",
    cta: "Скопировать ссылку",
  },
  expert_card: {
    title: "Заполни карточку эксперта",
    text: "Расскажи, чем помогаешь, и тебя найдут в каталоге",
    cta: "Заполнить",
  },
  circle_join: {
    title: "Найди сообщество со своей аудиторией",
    text: "Вступи в сообщества по своей теме",
    cta: "Выбрать сообщество",
  },
  meet_profile: { title: "Заполни профиль", text: "Фото, чем занимаешься, контакт", cta: "Заполнить" },
  ask: { title: "Задай вопрос своим", cta: "Задать вопрос" },
  hire: { title: "Размести вакансию или заказ", cta: "Разместить" },
  market_open: { title: "Загляни в барахолку", cta: "Открыть" },
  expert_request: {
    title: "Собери аудиторию для своего мероприятия",
    text: "Оставь запрос, и организаторы пришлют предложения",
    cta: "Оставить запрос",
  },
  hosts_enable: {
    title: "Включи приём запросов от экспертов",
    text: "Эксперты ищут организаторов, и ты увидишь их запросы",
    cta: "Включить",
  },
  circle_experts: CIRCLE_EXPERTS_STEP,
};

export type SectionHintKey = "events" | "circles" | "experts" | "people" | "expert_requests";
export const SECTION_HINTS: Record<SectionHintKey, string> = {
  events:
    "Здесь все события по Турции. Выбери город и дату, а записаться можно прямо со страницы события.",
  circles: "Здесь живут движения и клубы. Вступай, чтобы видеть их события и участников.",
  experts:
    "Здесь люди, которые помогают своим: документы, здоровье, недвижимость и не только. Пиши напрямую.",
  expert_requests: ER_HINT,
  people:
    "Здесь я подбираю людей с общими интересами. Пригласи на кофе, если хочешь познакомиться вживую.",
};
